import React from 'react';

const ApprovalPrintA4Internal = ({ approval, customer, reminder, termsAndConditions }) => {
  if (!approval) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not Set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return `${value.length} items`;
    if (typeof value === 'object') {
      if (value.mode && value.amount) {
        return `${value.mode}: ₹${value.amount}`;
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  const formatFieldName = (field) => {
    const fieldMap = {
      'approval_date': 'Approval Date',
      'return_date': 'Return Date',
      'base_24k_rate': 'Base Rate',
      'customer_id': 'Customer',
      'management_signature': 'Management Signature',
      'customer_signature': 'Customer Signature'
    };

    return fieldMap[field] || field
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Calculate totals
  const calculateTotals = () => {
    const items = approval.items || [];
    // Weight is total weight for all pieces — quantity is informational only, NOT a multiplier
    const totalWeight = items.reduce((sum, item) => {
      if (item.gross_wt) return sum + parseFloat(item.gross_wt);
      return sum + (parseFloat(item.weight_fixed) || parseFloat(item.weight) || 0);
    }, 0);
    const totalNetWeight = items.reduce((sum, item) => {
      if (item.net_wt) return sum + parseFloat(item.net_wt);
      if (item.gross_wt) return sum + parseFloat(item.gross_wt);
      return sum + (parseFloat(item.weight_fixed) || parseFloat(item.weight) || 0);
    }, 0);
    const totalFineWeight = items.reduce((sum, item) => {
      let netWt = 0;
      if (item.net_wt) netWt = parseFloat(item.net_wt);
      else if (item.gross_wt) netWt = parseFloat(item.gross_wt);
      else netWt = parseFloat(item.weight_fixed) || parseFloat(item.weight) || 0;
      const purity = parseFloat(item.purity) || 100;
      return sum + (netWt * purity / 100);
    }, 0);
    
    const payments = approval.advance_payments || [];
    const totalAdvance = payments.reduce((sum, p) => {
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);
    
    let estimatedValue = 0;
    if (approval.base_24k_rate) {
      estimatedValue = totalFineWeight * parseFloat(approval.base_24k_rate);
      if (approval.gst_applicable && approval.gst_input_style === 'EXCL') {
        estimatedValue *= (1 + (approval.gst_percent || 3) / 100);
      }
    }
    
    return { totalWeight, totalNetWeight, totalFineWeight, totalAdvance, estimatedValue };
  };

  const totals = calculateTotals();

  // Group payments by mode
  const groupPaymentsByMode = () => {
    const grouped = {};
    (approval.advance_payments || []).forEach(payment => {
      const mode = payment.mode || 'Unknown';
      if (!grouped[mode]) {
        grouped[mode] = [];
      }
      grouped[mode].push(payment);
    });
    return grouped;
  };

  const paymentsByMode = groupPaymentsByMode();

  const renderRevisionChange = (change, idx) => {
    const isPhotosField = change.field === 'photos';
    const oldPhotos = isPhotosField && Array.isArray(change.old_value) ? change.old_value : [];
    const newPhotos = isPhotosField && Array.isArray(change.new_value) ? change.new_value : [];
    
    if (isPhotosField) {
      const photosAdded = newPhotos.length - oldPhotos.length;
      const action = photosAdded > 0 ? 'Photo(s) Added' : photosAdded < 0 ? 'Photo(s) Removed' : 'Photos Changed';
      const color = photosAdded > 0 ? '#059669' : photosAdded < 0 ? '#dc2626' : '#7c3aed';
      
      return (
        <div key={idx} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '3px', color: color }}>
            📷 {action}
          </div>
          <div style={{ fontSize: '9px' }}>
            <span style={{ color: '#dc2626' }}>{oldPhotos.length} photo{oldPhotos.length !== 1 ? 's' : ''}</span>
            <span style={{ margin: '0 5px' }}>→</span>
            <span style={{ color: '#059669' }}>{newPhotos.length} photo{newPhotos.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      );
    }
    
    const isSignature = change.field === 'management_signature' || change.field === 'customer_signature';
    
    return (
      <div key={idx} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '3px' }}>{formatFieldName(change.field)}</div>
        {isSignature ? (
          <div style={{ fontSize: '9px' }}>Signature updated</div>
        ) : (
          <div style={{ fontSize: '9px' }}>
            <span style={{ color: '#dc2626' }}>Old: {formatValue(change.old_value)}</span>
            <span style={{ margin: '0 5px' }}>→</span>
            <span style={{ color: '#059669' }}>New: {formatValue(change.new_value)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="approval-a4-internal-print-content" className="print-content-hidden">
      {approval?.has_provisional_changes && <div className="provisional-watermark">PROVISIONAL</div>}
      <div style={{ 
        fontFamily: 'Arial, sans-serif',
        fontSize: '10px',
        lineHeight: '1.3',
        color: '#000',
        padding: '10px',
        maxWidth: '100%'
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '3px', margin: '0' }}>APPROVAL DETAILS (INTERNAL)</h1>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>Approval ID: {approval.approval_id}</div>
          <div style={{ fontSize: '11px' }}>Date: {formatDate(approval.approval_date || approval.created_at)} | Status: {approval.status}</div>
          <div style={{ fontSize: '9px', color: '#dc2626', marginTop: '3px' }}>⚠️ INTERNAL USE ONLY - NOT FOR CUSTOMER</div>
        </div>

        {/* Customer Info */}
        <div style={{ marginBottom: '15px', border: '1px solid #000', padding: '8px', borderRadius: '4px' }}>
          <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #ddd', paddingBottom: '3px', margin: '0 0 6px 0' }}>👤 CUSTOMER</h2>
          {customer && (
            <div style={{ fontSize: '9px', lineHeight: '1.4' }}>
              <div><strong>Name:</strong> {customer.name}</div>
              {customer.mobile && <div><strong>Mobile:</strong> {customer.mobile}</div>}
              {customer.alternate_mobile && <div><strong>Alt:</strong> {customer.alternate_mobile}</div>}
              {customer.email && <div><strong>Email:</strong> {customer.email}</div>}
              {customer.address && <div><strong>Address:</strong> {customer.address}</div>}
            </div>
          )}
        </div>

        {/* Rate Info */}
        {approval.base_24k_rate && (
          <div style={{ marginBottom: '15px', border: '1px solid #f59e0b', padding: '8px', borderRadius: '4px', backgroundColor: '#fffbeb' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #f59e0b', paddingBottom: '3px', margin: '0 0 6px 0' }}>💰 RATE INFO</h2>
            <div style={{ fontSize: '9px', lineHeight: '1.4' }}>
              <div><strong>24K Rate:</strong> ₹{formatCurrency(approval.base_24k_rate)}/g</div>
              {approval.gst_applicable === false ? (
                <div><strong>GST:</strong> Kaccha (No GST)</div>
              ) : (
                <div><strong>GST:</strong> {approval.gst_percent || 3}% {approval.gst_input_style === 'INCL' ? '(Incl)' : '(Excl)'}</div>
              )}
            </div>
          </div>
        )}

        {/* Items */}
        {approval.items && approval.items.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 6px 0' }}>📦 ITEMS ({approval.items.length})</h2>
            {approval.items.map((item, idx) => (
              <div key={idx} style={{ marginBottom: '8px', border: '1px solid #ddd', padding: '5px', borderRadius: '4px', backgroundColor: '#f9fafb', pageBreakInside: 'avoid' }}>
                <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '3px' }}>Item #{idx + 1}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '6px' }}>
                  {/* Item Details */}
                  <div style={{ fontSize: '8px', lineHeight: '1.4' }}>
                    <div><strong>Name:</strong> {item.item_type}</div>
                    {item.tag_number && <div><strong>Tag:</strong> {item.tag_number}</div>}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginTop: '2px' }}>
                      {item.gross_wt ? (
                        <>
                          <div><strong>Gross Wt:</strong> {parseFloat(item.gross_wt).toFixed(3)}g</div>
                          <div><strong>Less Wt:</strong> {item.less_wt ? parseFloat(item.less_wt).toFixed(3) : '0.000'}g</div>
                          <div><strong>Net Wt:</strong> {item.net_wt ? parseFloat(item.net_wt).toFixed(3) : parseFloat(item.gross_wt).toFixed(3)}g</div>
                        </>
                      ) : (
                        <>
                          {item.weight_type === 'Fixed' && item.weight_fixed && (
                            <div><strong>Weight:</strong> {item.weight_fixed}g</div>
                          )}
                          {item.weight_type === 'Range' && (
                            <div><strong>Weight:</strong> {item.weight_min}-{item.weight_max}g</div>
                          )}
                        </>
                      )}
                      {item.purity && <div><strong>Purity:</strong> {item.purity}</div>}
                      {item.quantity && <div><strong>Qty:</strong> {item.quantity}</div>}
                      {item.size && <div><strong>Size:</strong> {item.size}</div>}
                    </div>
                    {item.public_remark && (
                      <div style={{ marginTop: '3px', fontSize: '7px' }}><strong>Note:</strong> {item.public_remark}</div>
                    )}
                    {item.internal_remark && (
                      <div style={{ marginTop: '3px', padding: '3px', backgroundColor: '#fef3c7', borderLeft: '2px solid #f59e0b', fontSize: '7px' }}>
                        <strong style={{ color: '#dc2626' }}>Internal:</strong> {item.internal_remark}
                      </div>
                    )}
                  </div>
                  {/* Item Photos - Bigger size */}
                  {item.photos && item.photos.length > 0 && (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: item.photos.length === 1 ? '1fr' : 'repeat(2, 1fr)', gap: '3px' }}>
                        {item.photos.slice(0, 4).map((photo, photoIdx) => (
                          <img
                            key={photoIdx}
                            src={photo}
                            alt={`${idx + 1}-${photoIdx + 1}`}
                            style={{ width: '100%', height: '85px', objectFit: 'cover', borderRadius: '2px', border: '1px solid #ddd' }}
                          />
                        ))}
                      </div>
                      {item.photos.length > 4 && (
                        <div style={{ fontSize: '7px', textAlign: 'center', marginTop: '2px' }}>+{item.photos.length - 4} more</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Approval Summary */}
        <div style={{ marginBottom: '15px', border: '2px solid #7c3aed', padding: '8px', borderRadius: '4px', backgroundColor: '#f5f3ff' }}>
          <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', margin: '0 0 6px 0' }}>📊 APPROVAL SUMMARY</h2>
          <div style={{ fontSize: '9px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span>Total Items:</span>
              <span style={{ fontWeight: 'bold' }}>{approval.items?.length || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span>Total Gross Weight:</span>
              <span style={{ fontWeight: 'bold' }}>{totals.totalWeight.toFixed(2)}g</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span>Total Net Weight:</span>
              <span style={{ fontWeight: 'bold' }}>{totals.totalNetWeight.toFixed(2)}g</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span>Total Fine Weight:</span>
              <span style={{ fontWeight: 'bold' }}>{totals.totalFineWeight.toFixed(2)}g</span>
            </div>
            {approval.base_24k_rate && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #7c3aed' }}>
                <span>Estimated Value:</span>
                <span style={{ fontWeight: 'bold', color: '#7c3aed' }}>₹{formatCurrency(totals.estimatedValue)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #7c3aed' }}>
              <span>Total Advance Received:</span>
              <span style={{ fontWeight: 'bold', color: '#059669' }}>₹{formatCurrency(totals.totalAdvance)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
              <span>Balance Pending:</span>
              <span style={{ fontWeight: 'bold', color: '#dc2626' }}>₹{formatCurrency(totals.estimatedValue - totals.totalAdvance)}</span>
            </div>
          </div>
        </div>

        {/* Payment Reminder */}
        {reminder && (
          <div style={{ marginBottom: '15px', border: '1px solid #f59e0b', padding: '8px', borderRadius: '4px', backgroundColor: '#fef3c7' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', margin: '0 0 6px 0' }}>⏰ PAYMENT REMINDER</h2>
            <div style={{ fontSize: '9px' }}>
              <div><strong>Due Date:</strong> {formatDate(reminder.due_at)}</div>
              <div><strong>Status:</strong> {reminder.status}</div>
              {reminder.notes && <div><strong>Notes:</strong> {reminder.notes}</div>}
            </div>
          </div>
        )}

        {/* Payments */}
        {approval.advance_payments && approval.advance_payments.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 8px 0' }}>💳 PAYMENT TRANSACTIONS</h2>

            {Object.keys(paymentsByMode).map((mode, modeIdx) => (
              <div key={modeIdx} style={{ marginBottom: '8px', border: '1px solid #ddd', padding: '6px', borderRadius: '4px' }}>
                <h3 style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px', color: '#059669', margin: '0 0 4px 0' }}>{mode}</h3>
                <div style={{ fontSize: '8px' }}>
                  {paymentsByMode[mode].map((payment, payIdx) => {
                    const amount = parseFloat(payment.amount) || 0;
                    const isRefund = payment.transaction_type === 'REFUND';
                    return (
                      <div key={payIdx} style={{ marginBottom: '3px', color: isRefund ? '#dc2626' : '#000' }}>
                        {formatDate(payment.date)} | {isRefund ? '-' : ''}₹{formatCurrency(Math.abs(amount))}
                        {isRefund && ' (REFUND)'}
                        {payment.note && ` | Note: ${payment.note}`}
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', fontSize: '9px' }}>
                  Total: ₹{formatCurrency(
                    paymentsByMode[mode].reduce((sum, p) => {
                      const amount = parseFloat(p.amount) || 0;
                      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
                    }, 0)
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Signatures */}
        {(approval.management_signature || approval.customer_signature) && (
          <div style={{ marginBottom: '15px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 6px 0' }}>✍️ SIGNATURES</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {approval.management_signature && (
                <div style={{ border: '1px solid #ddd', padding: '6px', borderRadius: '4px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '4px' }}>Management</div>
                  <img src={approval.management_signature} alt="Management" style={{ width: '100%', maxWidth: '120px', height: 'auto', maxHeight: '40px', border: '1px solid #ddd' }} />
                  {approval.management_signature_name && <div style={{ fontSize: '8px', marginTop: '3px' }}>Name: {approval.management_signature_name}</div>}
                </div>
              )}
              {approval.customer_signature && (
                <div style={{ border: '1px solid #ddd', padding: '6px', borderRadius: '4px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '4px' }}>Customer</div>
                  <img src={approval.customer_signature} alt="Customer" style={{ width: '100%', maxWidth: '120px', height: 'auto', maxHeight: '40px', border: '1px solid #ddd' }} />
                  {customer && customer.name && <div style={{ fontSize: '8px', marginTop: '3px' }}>Name: {customer.name}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revision History - Full Detail, No Cropping */}
        {approval.revision_history && approval.revision_history.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 8px 0' }}>📝 REVISION HISTORY ({approval.revision_history.length})</h2>
            <div style={{ fontSize: '9px', lineHeight: '1.5' }}>
              {approval.revision_history.map((revision, idx) => {
                const changes = revision.changes || [];
                return (
                  <div key={idx} style={{ marginBottom: '10px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fef3c7', pageBreakInside: 'avoid' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '10px' }}>
                      Revision #{revision.revision_number || idx + 1} - {formatDate(revision.timestamp)}
                    </div>
                    <div style={{ marginBottom: '5px', fontSize: '9px' }}>
                      <strong>By:</strong> {revision.user || 'Unknown'}
                    </div>
                    {changes.length > 0 && (
                      <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e5e7eb' }}>
                        {changes.map((change, changeIdx) => (
                          <div key={changeIdx} style={{ marginBottom: '6px', paddingLeft: '10px', borderLeft: '3px solid #f59e0b' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '9px', marginBottom: '2px' }}>
                              {formatFieldName(change.field)}
                            </div>
                            <div style={{ fontSize: '8px', color: '#374151', lineHeight: '1.4' }}>
                              <div style={{ marginBottom: '1px' }}>
                                <span style={{ color: '#dc2626' }}>Old:</span> {formatValue(change.old_value)}
                              </div>
                              <div>
                                <span style={{ color: '#059669' }}>New:</span> {formatValue(change.new_value)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Terms and Conditions */}
        <div style={{ marginBottom: '10px', pageBreakInside: 'avoid' }}>
          <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 4px 0' }}>📋 TERMS & CONDITIONS</h2>
          <div style={{ fontSize: '7px', lineHeight: '1.4', whiteSpace: 'pre-wrap', padding: '4px', backgroundColor: '#f9fafb', border: '1px solid #ddd', borderRadius: '2px' }}>
            {termsAndConditions || 'No terms and conditions set. Please contact management for details.'}
          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '10px', paddingTop: '6px', borderTop: '1px solid #000', textAlign: 'center', fontSize: '8px', color: '#6b7280' }}>
          <div style={{ color: '#dc2626', fontWeight: 'bold', marginBottom: '3px' }}>⚠️ INTERNAL DOCUMENT - CONFIDENTIAL</div>
          <div>Computer-generated document | Printed: {new Date().toLocaleString('en-IN')}</div>
        </div>

      </div>
    </div>
  );
};

export default ApprovalPrintA4Internal;